import { useState, useRef, useEffect } from "react"
import { useMediaQuery } from "react-responsive"

import PropTypes from "prop-types"

import { EffectFade, Thumbs, Navigation, FreeMode } from "swiper"

import "swiper/css"
import "swiper/css/effect-fade"
import "swiper/css/pagination"
import "swiper/css/thumbs"
import { Swiper, SwiperSlide } from "swiper/react"

import { Heading } from "../../components/Heading/Heading"
import LinkButton from "../../components/LinkButton/LinkButton"
import ThumbsPagination from "./ThumbsPagination"

import styles from "./ListOfSlides.module.scss"

const ListOfSlides = ({
  slides,
  textOverImage,
  videoAutoplayChain = false,
  controls = false,
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const mainSwiperRef = useRef(null)
  const [videoDuration, setVideoDuration] = useState(0)

  useEffect(() => {
    if (!videoAutoplayChain) return
    const swiper = mainSwiperRef.current?.swiper
    if (!swiper) return

    const handleSlideChange = () => {
      const allVideos = document.querySelectorAll(`.${styles.slide} video`)
      allVideos.forEach(v => {
        v.pause()
        v.currentTime = 0
      })

      const currentSlide = swiper.slides[swiper.activeIndex]
      const video = currentSlide?.querySelector("video")

      if (video) {
        video.play().catch(() => {})
        if (video.readyState >= 1) {
          setVideoDuration(video.duration)
        } else {
          video.onloadedmetadata = () => {
            setVideoDuration(video.duration)
          }
        }

        video.onended = () => {
          if (swiper.isEnd) {
            swiper.slideTo(0)
          } else {
            swiper.slideNext()
          }
        }
      }
    }

    swiper.on("slideChange", handleSlideChange)
    handleSlideChange()

    return () => {
      swiper.off("slideChange", handleSlideChange)
    }
  }, [videoAutoplayChain])

  const isMobile = useMediaQuery({ query: "(max-width: 767px)" })
  const isTablet = useMediaQuery({ query: "(max-width: 1023px)" })
  const isMinDesk = useMediaQuery({ query: "(max-width: 1536px)" })

  const perPageThumb = isMobile ? 1.5 : isTablet ? 2.5 : isMinDesk ? 2.5 : 3

  const renderMedia = (media, index) => {
    if (!media || !media.type) return null
    const isActive = videoAutoplayChain && index === activeSlideIndex

    if (media.type === "image") {
      return <img src={media.src} alt={media.alt || ""} />
    }

    if (media.type === "video") {
      return (
        <video
          muted
          playsInline
          controls={controls}
          autoPlay={isActive}
          className={styles.video}
        >
          <source src={media.src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )
    }

    return null
  }

  const renderThumbs = () => {
    return (
      <div className={styles.thumbContainer}>
        <Swiper
          onSwiper={setThumbsSwiper}
          slidesPerView={perPageThumb}
          mousewheel={false}
          navigation={false}
          className={styles.thumbsSwiper}
          allowTouchMove={isTablet || isMobile}
          watchSlidesProgress={true}
          modules={[FreeMode, Navigation, Thumbs]}
          freeMode={false}
          spaceBetween={6}
          loop={false}
          breakpoints={{
            420: {
              spaceBetween: 20,
            },
          }}
        >
          {slides.map((item, index) => {
            const displayMedia =
              isMobile && item.mobileMedia ? item.mobileMedia : item.media
            const isActive = index === activeSlideIndex

            return (
              <SwiperSlide
                key={`thumb_${item.id}`}
                className={`${styles.thumbSlider} ${
                  isActive ? styles.active : ""
                }`}
              >
                <ThumbsPagination
                  title={item.title || ""}
                  intro={item.description || ""}
                  media={displayMedia}
                  poster={item.poster || ""}
                  isMobile={isMobile || isTablet}
                  isActive={isActive}
                  duration={isActive ? videoDuration : 0}
                />
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
    )
  }

  const renderCard = (title, description, link, textButton) => (
    <div className={styles.contenData}>
      <div className={styles.textPrincipal}>
        <div className={styles.cardHero}>
          <Heading tag="h2" size="d-xxl">
            {title}
          </Heading>
          {description && <p>{description}</p>}
        </div>
        {link && textButton && (
          <div className={styles.link}>
            <LinkButton
              className="banner-link"
              displayBlock={true}
              styleVariant="primary"
              to={link}
            >
              {textButton}
            </LinkButton>
          </div>
        )}
      </div>
    </div>
  )

  const renderSlider = () => (
    <>
      <Swiper
        ref={mainSwiperRef}
        effect="fade"
        loop={false}
        grabCursor={true}
        onSlideChange={swiper => setActiveSlideIndex(swiper.realIndex)}
        thumbs={{
          swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
        }}
        modules={[EffectFade, Navigation, Thumbs]}
        slidesPerView={1}
        key="parent"
        className={styles.swiper}
      >
        {slides.map((item, index) => {
          const displayMedia =
            isMobile && item.mobileMedia ? item.mobileMedia : item.media
          return (
            <SwiperSlide key={item.id} className={styles.slide}>
              {textOverImage && (
                <div className={styles.textContent}>
                  {renderCard(
                    item.title,
                    item.description,
                    item.button?.uri,
                    item.button?.title
                  )}
                </div>
              )}
              <div className={styles.mediaContainer}>
                {renderMedia(displayMedia, index)}
              </div>
            </SwiperSlide>
          )
        })}
      </Swiper>
      {renderThumbs()}
    </>
  )

  return (
    <section className={styles.hero}>
      <div className={styles.content}>{renderSlider()}</div>
    </section>
  )
}

ListOfSlides.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string,
      description: PropTypes.string,
      media: PropTypes.shape({
        type: PropTypes.oneOf(["image", "video"]).isRequired,
        src: PropTypes.string.isRequired,
        alt: PropTypes.string,
      }),
      mobileMedia: PropTypes.shape({
        type: PropTypes.oneOf(["image", "video"]),
        src: PropTypes.string,
        alt: PropTypes.string,
      }),
      button: PropTypes.shape({
        uri: PropTypes.string,
        title: PropTypes.string,
      }),
    })
  ).isRequired,
}

export default ListOfSlides
